/**
 * RebuildAuditCard — the "your rewrite / v2-dashboard didn't save"
 * banner. Renders **null** unless the audit actually needs a rebuild.
 *
 * When the user clicks "Rebuild full audit":
 *   • Kicks the backend job (POST /analyze/{id}/rebuild-blueprint),
 *     which returns { job_id, est_seconds_total }.
 *   • Replaces the CTA button with a live **countdown timer** + circular
 *     progress ring on the right side. The timer counts DOWN from the
 *     backend's estimate; a "stages" strip below reflects real progress
 *     (blueprint / v2 running / complete) from the polled job doc.
 *   • On complete, reloads the analysis page.
 *   • On error, surfaces the retry CTA again with the reason.
 *
 * Fail-proof design:
 *   • Backend now uses gpt-5-mini (fast tier) capped at 180s per branch.
 *   • Client polls every 3 s with a hard 8-minute cap after which we
 *     surface a retry banner instead of spinning forever.
 */
import React, { useEffect, useMemo, useRef, useState } from 'react';
import { toast } from 'sonner';
import { RefreshCw, Sparkles, ArrowRight, AlertTriangle, Check, Clock } from 'lucide-react';
import { Button } from '../ui/button';
import { linkedge } from '../../lib/api';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const POLL_MS = 3000;
const CLIENT_MAX_MS = 8 * 60 * 1000; // 8-minute safety net

function _isBlueprintEmpty(bp) {
  if (!bp) return true;
  const headline = bp.headline || {};
  const about = bp.about || {};
  const skills = bp.skills || {};
  const experiences = bp.experiences || [];
  return (
    !headline.variants?.length &&
    !about.suggested &&
    !about.current &&
    (!experiences || experiences.length === 0) &&
    (!skills || (!skills.suggested?.length && !skills.priority?.length))
  );
}

export function computeRebuildNeed(data) {
  if (!data) return { needed: false };
  const rewriteMissing = _isBlueprintEmpty(data?.blueprint);
  const v2Missing = !!(data?.audit_context_v2?.career_stage) && !data?.full_audit_v2;
  return { needed: rewriteMissing || v2Missing, rewriteMissing, v2Missing };
}

function formatMMSS(seconds) {
  const s = Math.max(0, Math.ceil(seconds));
  const m = Math.floor(s / 60);
  const r = s % 60;
  return `${m}:${r.toString().padStart(2, '0')}`;
}

// Compact SVG progress ring that renders on the right side of the card.
function CountdownRing({ remaining, total, stage }) {
  const size = 82;
  const stroke = 6;
  const r = (size - stroke) / 2;
  const c = 2 * Math.PI * r;
  const pct = total > 0 ? Math.min(1, Math.max(0, 1 - remaining / total)) : 0;
  return (
    <div className="relative shrink-0" style={{ width: size, height: size }} data-testid="rebuild-countdown-ring">
      <svg width={size} height={size} className="transform -rotate-90">
        <circle
          cx={size / 2}
          cy={size / 2}
          r={r}
          stroke="rgba(245,158,11,0.20)"
          strokeWidth={stroke}
          fill="none"
        />
        <circle
          cx={size / 2}
          cy={size / 2}
          r={r}
          stroke="#F59E0B"
          strokeWidth={stroke}
          strokeLinecap="round"
          strokeDasharray={c}
          strokeDashoffset={c - pct * c}
          fill="none"
          style={{ transition: 'stroke-dashoffset 0.9s linear' }}
        />
      </svg>
      <div className="absolute inset-0 flex flex-col items-center justify-center">
        <span className="font-mono-edge text-[15px] font-semibold tabular-nums text-amber-900 dark:text-amber-200">
          {formatMMSS(remaining)}
        </span>
        <span className="text-[9px] uppercase tracking-[0.18em] text-amber-700/80 dark:text-amber-300/70">
          {stage || 'left'}
        </span>
      </div>
    </div>
  );
}

function StageDot({ label, state }) {
  const isDone = state === 'complete';
  const isRunning = state === 'running';
  const isFailed = state === 'failed';
  const iconCls = isDone
    ? 'bg-emerald-500 text-white'
    : isRunning
      ? 'bg-amber-500 text-white'
      : isFailed
        ? 'bg-rose-500 text-white'
        : 'bg-slate-300 text-slate-500 dark:bg-slate-700 dark:text-slate-400';
  return (
    <span className="inline-flex items-center gap-1.5 text-[11.5px] text-slate-700 dark:text-slate-300">
      <span className={`inline-flex h-4 w-4 items-center justify-center rounded-full ${iconCls}`}>
        {isDone ? <Check size={9} strokeWidth={3} /> : isRunning ? <RefreshCw size={9} className="animate-spin" /> : isFailed ? <AlertTriangle size={9} /> : <span className="h-1 w-1 rounded-full bg-current" />}
      </span>
      {label}
    </span>
  );
}

export default function RebuildAuditCard({ data, className = '' }) {
  const { needed, rewriteMissing, v2Missing } = computeRebuildNeed(data);

  // Rebuild-run state (only meaningful while a job is in flight).
  const [job, setJob] = useState(null);              // { job_id, started_at (ms), est_seconds_total }
  const [stages, setStages] = useState({ blueprint: 'idle', v2: 'idle' });
  const [nowMs, setNowMs] = useState(() => Date.now());
  const [failed, setFailed] = useState(null);         // { message }
  const pollRef = useRef(null);
  const tickRef = useRef(null);

  // 1-second tick so the countdown feels live.
  useEffect(() => {
    if (!job) return;
    tickRef.current = setInterval(() => setNowMs(Date.now()), 1000);
    return () => clearInterval(tickRef.current);
  }, [job]);

  // Poll job status every POLL_MS while a job is in flight.
  useEffect(() => {
    if (!job) return;
    let cancelled = false;
    const startedAt = job.started_at;

    async function tick() {
      try {
        const token = localStorage.getItem('le_token') || '';
        const { data: j } = await axios.get(`${API}/jobs/${job.job_id}`, {
          headers: { Authorization: `Bearer ${token}` },
          timeout: 15000,
        });
        if (cancelled) return;
        setStages({
          blueprint: j?.stage_blueprint || 'idle',
          v2: j?.stage_v2 || 'idle',
        });
        if (j?.status === 'complete') {
          const partial = j?.result?.partial_errors || [];
          const gotV2 = (j?.result?.sections || {}).full_audit_v2;
          if (partial.length && !gotV2) {
            _stop();
            setFailed({ message: 'The v2 engine timed out again — try Rebuild once more.' });
            toast.error('Rebuild finished but the v2 layout could not be built. Try again.', { duration: 6000 });
            return;
          }
          _stop();
          toast.success('Audit rebuilt — reloading…');
          setTimeout(() => window.location.reload(), 700);
          return;
        }
        if (j?.status === 'error') {
          _stop();
          setFailed({ message: j?.error || 'Rebuild failed. Try again.' });
          toast.error(j?.error || 'Rebuild failed. Try again.');
          return;
        }
        if (Date.now() - startedAt > CLIENT_MAX_MS) {
          _stop();
          setFailed({ message: 'Rebuild is taking longer than expected. Refresh in a minute or try again.' });
          toast.error('Rebuild taking too long — please try again.');
        }
      } catch (e) {
        // Transient network errors: keep polling until the safety net trips.
        if (Date.now() - startedAt > CLIENT_MAX_MS) {
          _stop();
          setFailed({ message: 'Lost connection to the rebuild job. Please retry.' });
        }
      }
    }

    function _stop() {
      cancelled = true;
      if (pollRef.current) clearInterval(pollRef.current);
      if (tickRef.current) clearInterval(tickRef.current);
      setJob(null);
    }

    pollRef.current = setInterval(tick, POLL_MS);
    tick();
    return () => {
      cancelled = true;
      if (pollRef.current) clearInterval(pollRef.current);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [job?.job_id]);

  const remainingSec = useMemo(() => {
    if (!job) return 0;
    const total = job.est_seconds_total || 180;
    const elapsed = (nowMs - job.started_at) / 1000;
    return Math.max(0, total - elapsed);
  }, [job, nowMs]);

  if (!needed) return null;

  const heading = v2Missing && rewriteMissing
    ? 'Your rewrite content and the new-dashboard data didn’t save on this audit.'
    : v2Missing
      ? 'Your new-dashboard audit didn’t save on this audit.'
      : 'Your rewrite content didn’t save on this audit.';

  const bodyIdle = v2Missing && rewriteMissing
    ? 'Both the profile rewrites (headline variants, About, Experience, Skills) and the new dashboard layout data timed out during the original audit. Rebuild now — the fast-tier engine finishes in ~3 minutes.'
    : v2Missing
      ? 'The new-dashboard audit engine timed out during creation. Rebuild it now to switch this audit to the new dropdown dashboard — ~3 minutes on the fast tier.'
      : 'The scorecard is here, but the headline-variants, About, Experience and Skills rewrite timed out during the original audit. Rebuild it now — ~2 minutes on the fast tier.';

  const bodyRunning = 'Sit tight — we\'re rebuilding your rewrite and the new-dashboard data on the fast-tier engine. The page will refresh automatically the moment it\'s ready.';

  const isRunning = !!job;

  const handleRebuild = async () => {
    if (isRunning) return;
    setFailed(null);
    // Optimistically flip both stages to running so the strip lights up
    // before the first poll returns.
    setStages({
      blueprint: rewriteMissing ? 'running' : 'skipped',
      v2: v2Missing ? 'running' : 'skipped',
    });
    try {
      const result = await linkedge.rebuildBlueprint(data?.id, { skipPolling: true });
      if (!result?.job_id) {
        // Backwards-compat: some old clients return the completed job doc directly.
        toast.success('Audit rebuilt — reloading…');
        setTimeout(() => window.location.reload(), 700);
        return;
      }
      setJob({
        job_id: result.job_id,
        started_at: Date.now(),
        est_seconds_total: result.est_seconds_total || 180,
      });
    } catch (e) {
      const msg = e?.message || 'Rebuild failed';
      setFailed({ message: msg });
      toast.error(msg);
      setStages({ blueprint: 'idle', v2: 'idle' });
    }
  };

  return (
    <div
      className={`relative overflow-hidden rounded-2xl ring-1 ring-amber-300/70 dark:ring-amber-400/30 bg-gradient-to-r from-amber-50 via-white to-rose-50 dark:from-amber-500/10 dark:via-slate-900/60 dark:to-rose-500/10 shadow-[0_1px_2px_rgba(15,23,42,0.04),0_10px_28px_-18px_rgba(245,158,11,0.35)] ${!isRunning ? 'animate-le-guide-glow' : ''} mb-6 ${className}`}
      data-testid="profile-rewrite-rebuild-card"
    >
      {!isRunning && (
        <span
          aria-hidden
          className="pointer-events-none absolute inset-y-0 -left-24 w-24 bg-gradient-to-r from-transparent via-white/70 to-transparent dark:via-white/10 blur-sm animate-le-guide-shimmer"
        />
      )}
      <div className="relative p-5 sm:p-6 flex flex-col sm:flex-row gap-4 sm:items-center justify-between">
        <div className="flex items-start gap-3 min-w-0">
          <span className="relative inline-flex h-10 w-10 items-center justify-center rounded-xl bg-amber-500 text-white shrink-0 shadow-[0_4px_16px_-4px_rgba(245,158,11,0.55)]">
            <AlertTriangle size={18} />
            {!isRunning && (
              <span className="absolute -top-0.5 -right-0.5 flex h-2.5 w-2.5">
                <span className="absolute inline-flex h-full w-full rounded-full bg-rose-400 opacity-75 animate-ping" />
                <span className="relative inline-flex h-2.5 w-2.5 rounded-full bg-rose-500 ring-2 ring-white dark:ring-slate-900" />
              </span>
            )}
          </span>
          <div className="min-w-0">
            <p className="font-mono-edge text-[10px] tracking-[0.22em] uppercase text-amber-700 dark:text-amber-400 flex items-center gap-1.5">
              {isRunning ? 'Rebuilding' : 'Action needed'}
              <span className={`inline-flex items-center rounded-full text-white text-[9.5px] font-semibold uppercase tracking-wider px-1.5 py-0.5 shadow-sm ${isRunning ? 'bg-emerald-500' : 'bg-amber-500'}`}>
                {isRunning ? 'live' : 'Rebuild'}
              </span>
            </p>
            <h3 className="text-base sm:text-lg font-semibold mt-0.5 leading-snug">
              {heading}
            </h3>
            <p className="text-[13.5px] text-slate-700 dark:text-slate-300 mt-1.5 leading-relaxed">
              {failed?.message || (isRunning ? bodyRunning : bodyIdle)}
            </p>

            {/* stages strip while running */}
            {isRunning && (
              <div className="mt-3 flex items-center flex-wrap gap-x-4 gap-y-1" data-testid="rebuild-stages">
                {rewriteMissing && (
                  <StageDot label="Profile rewrite" state={stages.blueprint} />
                )}
                {v2Missing && (
                  <StageDot label="New-dashboard audit" state={stages.v2} />
                )}
                <span className="inline-flex items-center gap-1 text-[11px] text-amber-700/80 dark:text-amber-300/70">
                  <Clock size={10} /> Fast-tier engine (gpt-5-mini)
                </span>
              </div>
            )}
          </div>
        </div>

        {/* Right side — countdown ring OR the CTA button */}
        {isRunning ? (
          <CountdownRing
            remaining={remainingSec}
            total={job?.est_seconds_total || 180}
            stage={remainingSec > 0 ? 'left' : 'wrapping'}
          />
        ) : (
          <Button
            onClick={handleRebuild}
            className="shrink-0 rounded-full bg-amber-500 hover:bg-amber-600 text-white shadow-[0_8px_20px_-8px_rgba(245,158,11,0.6)] focus-visible:ring-amber-400"
            size="lg"
            data-testid="profile-rebuild-blueprint-btn"
          >
            <Sparkles size={14} className="mr-2" />
            {failed ? 'Retry rebuild' : 'Rebuild full audit'}
            <ArrowRight size={13} className="ml-1.5 animate-le-guide-nudge" />
          </Button>
        )}
      </div>
    </div>
  );
}
